import React from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  finishActiveShiftAuditCommentPrompt,
  getActiveShiftAuditCommentPrompt,
  registerShiftAuditCommentPromptHost,
  subscribeToShiftAuditCommentPrompt,
} from "@/lib/shiftAuditCommentPromptStore"

export function ShiftUpdateCommentDialog() {
  const [request, setRequest] = React.useState(getActiveShiftAuditCommentPrompt())
  const [comment, setComment] = React.useState("")

  React.useEffect(() => {
    const unregisterHost = registerShiftAuditCommentPromptHost()
    const unsubscribe = subscribeToShiftAuditCommentPrompt(setRequest)

    return () => {
      unsubscribe()
      unregisterHost()
    }
  }, [])

  React.useEffect(() => {
    setComment("")
  }, [request?.id])

  const handleCancel = React.useCallback(() => {
    if (!request) return

    request.reject(new Error(request.missingCommentError))
    finishActiveShiftAuditCommentPrompt()
  }, [request])

  const handleConfirm = React.useCallback(() => {
    if (!request) return

    const normalizedComment = comment.trim()
    if (!normalizedComment) return

    request.resolve(normalizedComment)
    finishActiveShiftAuditCommentPrompt()
  }, [comment, request])

  return (
    <Dialog open={Boolean(request)} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{request?.title ?? "Add Audit Comment"}</DialogTitle>
          <DialogDescription>
            {request?.description ?? "Add a short note describing why this block is being changed."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="shift-update-comment">Comment</Label>
          <Textarea
            id="shift-update-comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={request?.placeholder ?? "Explain the change"}
            className="min-h-28 resize-none"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!comment.trim()}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
